// utils/payslipExcel.ts
// 薪資單 Excel for one person and one month, built from the /punch/monthly
// payload the 出勤時數 tab already holds (payroll is computed server-side in
// payroll.service.js — this file only lays it out, it never recomputes pay).
//
// exceljs (not SheetJS community, which cannot write styles) and loaded on
// click only, so it stays out of the main bundle. buildPayslipWorkbook is
// separate from the download so it can be exercised outside a browser.
import type { Workbook, Worksheet, Cell, Fill, Borders } from 'exceljs';
import { MonthlyUser, PayrollLine } from '../services/timeClockService';

type ExcelJSModule = typeof import('exceljs');

const COMPANY = '諾蘭國際股份有限公司';
const TAX_ID = '統一編號 93556799';
const WEEK = '日一二三四五六';

// Palette — matches the August payslip (scripts/hr/build_monthly_payroll.py).
const INK = 'FF2B3A4E';
const INK_LIGHT = 'FF5A6B7F';
const BAND = 'FFEEF1F5';
const LINE = 'FFC7CEDA';
const WHITE = 'FFFFFFFF';
const HOLIDAY_FILL = 'FFFFF4DC';
const SHORT_FILL = 'FFFDECEA';
const RED = 'FFB3261E';
const FONT = 'Microsoft JhengHei';
const MONEY = '#,##0';
const HRS = '0.00';

const solid = (argb: string): Fill => ({ type: 'pattern', pattern: 'solid', fgColor: { argb } });
const thin = { style: 'thin' as const, color: { argb: LINE } };
const BOX: Partial<Borders> = { top: thin, left: thin, bottom: thin, right: thin };

// Staff's own punch notes only — the owner's correction / backfill audit tags
// (「更正（Magnus 10/01）」) stay in the OMS, not on what the employee receives.
const staffNote = (note: string | null) =>
  (note || '').split(' · ').filter((part) => !part.includes('Magnus')).join(' · ');

const weekdayOf = (ymd: string) => {
  const [y, m, d] = ymd.split('-').map(Number);
  return WEEK[new Date(Date.UTC(y, m - 1, d)).getUTCDay()];
};

function put(
  ws: Worksheet,
  addr: string,
  value: Cell['value'],
  opts: {
    size?: number; bold?: boolean; color?: string; fill?: string; fmt?: string;
    align?: 'left' | 'center' | 'right'; border?: boolean; wrap?: boolean;
  } = {},
): Cell {
  const c = ws.getCell(addr);
  c.value = value;
  c.font = { name: FONT, size: opts.size ?? 10, bold: !!opts.bold, color: { argb: opts.color ?? INK } };
  if (opts.fill) c.fill = solid(opts.fill);
  if (opts.fmt) c.numFmt = opts.fmt;
  c.alignment = { horizontal: opts.align ?? 'left', vertical: 'middle', wrapText: !!opts.wrap };
  if (opts.border) c.border = BOX;
  return c;
}

// A white-on-ink header bar across [from..to] on one row.
function bar(ws: Worksheet, row: number, from: string, to: string, text: string, size = 10) {
  ws.mergeCells(`${from}${row}:${to}${row}`);
  put(ws, `${from}${row}`, text, { bold: true, color: WHITE, fill: INK, size, align: 'left' });
  ws.getRow(row).height = 22;
}

// Item name with its small grey qualifier ("時薪　141.41 小時").
const itemValue = (l: PayrollLine): Cell['value'] =>
  l.note
    ? {
      richText: [
        { text: l.name || l.label, font: { name: FONT, size: 10, color: { argb: INK } } },
        { text: `　${l.note}`, font: { name: FONT, size: 9, color: { argb: INK_LIGHT } } },
      ],
    }
    : (l.name || l.label);

function buildSlip(ws: Worksheet, user: MonthlyUser, month: string) {
  const p = user.payroll!;
  const name = user.employee_name || `User ${user.user_id}`;
  const [y, m] = month.split('-').map(Number);
  const isMonthly = p.type === 'monthly';

  ws.columns = [{ width: 2 }, { width: 30 }, { width: 16 }, { width: 3 }, { width: 30 }, { width: 20 }, { width: 2 }];
  ws.views = [{ showGridLines: false }];

  // Title block
  ws.mergeCells('B2:F2');
  put(ws, 'B2', '薪　資　單', { size: 20, bold: true, align: 'center' });
  ws.getRow(2).height = 36;
  ws.mergeCells('B3:F3');
  put(ws, 'B3', `${COMPANY}　${TAX_ID}`, { size: 9, color: INK_LIGHT, align: 'center' });

  // Identity block
  const info: [string, string, string, string][] = [
    ['員工姓名', name, '薪資期間', `${y} 年 ${m} 月`],
    ['薪制', isMonthly ? '月薪制' : '時薪制', '發薪日', ''],
  ];
  info.forEach(([k1, v1, k2, v2], i) => {
    const r = 5 + i;
    put(ws, `B${r}`, k1, { bold: true, fill: BAND, border: true });
    put(ws, `C${r}`, v1, { border: true, align: 'center' });
    put(ws, `E${r}`, k2, { bold: true, fill: BAND, border: true });
    put(ws, `F${r}`, v2, { border: true, align: 'center' });
    ws.getRow(r).height = 20;
  });

  // Earnings | deductions, side by side
  bar(ws, 8, 'B', 'C', '應發項目');
  bar(ws, 8, 'E', 'F', '代扣項目');
  const earn = p.lines.filter((l) => l.amount !== 0 || (l.name || l.label).startsWith('本薪'));
  const ded = p.deductions;
  const rows = Math.max(earn.length, ded.length, 3);
  for (let i = 0; i < rows; i++) {
    const r = 9 + i;
    const e = earn[i];
    const d = ded[i];
    put(ws, `B${r}`, e ? itemValue(e) : null, { border: true });
    put(ws, `C${r}`, e ? e.amount : null, { border: true, fmt: MONEY, align: 'right' });
    put(ws, `E${r}`, d ? itemValue(d) : null, { border: true });
    put(ws, `F${r}`, d ? d.amount : null, { border: true, fmt: MONEY, align: 'right' });
    ws.getRow(r).height = 20;
  }
  const tot = 9 + rows;
  const deductTotal = ded.reduce((s, d) => s + d.amount, 0);
  put(ws, `B${tot}`, '應發合計', { bold: true, fill: BAND, border: true });
  put(ws, `C${tot}`, p.gross, { bold: true, fill: BAND, border: true, fmt: MONEY, align: 'right' });
  put(ws, `E${tot}`, '代扣合計', { bold: true, fill: BAND, border: true });
  put(ws, `F${tot}`, deductTotal, { bold: true, fill: BAND, border: true, fmt: MONEY, align: 'right' });
  ws.getRow(tot).height = 22;

  // Net pay
  const net = tot + 2;
  ws.mergeCells(`B${net}:E${net}`);
  put(ws, `B${net}`, '實　發　金　額', { size: 13, bold: true, color: WHITE, fill: INK });
  put(ws, `F${net}`, p.net, { size: 14, bold: true, color: WHITE, fill: INK, fmt: `"NT$ "${MONEY}`, align: 'right' });
  ws.getRow(net).height = 32;

  // Attendance summary
  const att = net + 2;
  bar(ws, att, 'B', 'C', '本月出勤');
  const stats: [string, number, string][] = isMonthly
    ? [
      ['出勤時數', user.total_hours, HRS],
      ['工作時數', p.hours.worked ?? 0, HRS],
      ['早退時數', p.hours.short ?? 0, HRS],
      ['國定假日出勤', p.hours.holiday_days ?? 0, '0" 天"'],
    ]
    : [
      ['出勤時數', user.total_hours, HRS],
      ['一般時數', p.hours.regular ?? 0, HRS],
      ['國定假日時數', p.hours.holiday ?? 0, HRS],
    ];
  if (isMonthly && p.early_leave_offset) {
    // 早退 is offset by his longer weekdays (not paid as 加班); only the rest is deducted.
    stats.push(['平日延長工時（抵早退）', p.hours.weekday_extra ?? 0, HRS], ['早退淨扣時數', p.hours.short_net ?? 0, HRS]);
  } else if (isMonthly && p.weekday_overtime_paid === false) {
    stats.push(['平日加班（另行約定）', (p.hours.overtime_134 ?? 0) + (p.hours.overtime_167 ?? 0), HRS]);
  }
  stats.forEach(([k, v, fmt], i) => {
    const r = att + 1 + i;
    put(ws, `B${r}`, k, { border: true });
    put(ws, `C${r}`, v, { border: true, fmt, align: 'right' });
    ws.getRow(r).height = 18;
  });

  // Notes — how pay is computed, never how 勞健保 is.
  const notes = isMonthly
    ? [
      '· 每小時工資＝月薪 ÷ 240；在廠超過 8 小時才扣休息 10 分鐘，超過 8 小時 10 分鐘起算加班。',
      p.early_leave_offset
        ? '· 早退時數先以其他平日延長之工時抵扣，不足部分以時薪扣除。'
        : '· 平日工作未滿 8 小時且短少 20 分鐘以上者，以時薪扣除早退時數。',
      '· 國定假日出勤加發一日工資；未出勤之國定假日照常支薪。',
    ]
    : ['· 時薪 × 實際出勤時數；國定假日出勤時數以雙倍計薪。'];
  const n0 = att + stats.length + 2;
  notes.forEach((t, i) => {
    ws.mergeCells(`B${n0 + i}:F${n0 + i}`);
    put(ws, `B${n0 + i}`, t, { size: 9, color: INK_LIGHT });
  });

  const last = n0 + notes.length;
  ws.pageSetup = {
    paperSize: 9, orientation: 'portrait', fitToPage: true, fitToWidth: 1, fitToHeight: 1,
    horizontalCentered: true, margins: { left: 0.5, right: 0.5, top: 0.6, bottom: 0.6, header: 0.3, footer: 0.3 },
    printArea: `A1:G${last}`,
  };
}

function buildDetail(ws: Worksheet, user: MonthlyUser, month: string) {
  const p = user.payroll!;
  const name = user.employee_name || `User ${user.user_id}`;
  const [y, m] = month.split('-').map(Number);
  const isMonthly = p.type === 'monthly';
  const daysByDate = new Map((p.days || []).map((d) => [d.date, d]));
  const holidayDates = new Set(p.alerts.filter((a) => a.message.startsWith('國定假日')).map((a) => a.date));
  const rate = Number(p.lines[0]?.label.match(/× (\d+)/)?.[1] ?? 200);

  const headers = isMonthly
    ? ['日期', '星期', '上班', '下班', '在廠時數', '休息', '工作時數',
      p.weekday_overtime_paid === false ? '加班（平日另約）' : '加班', '早退時數', '早退扣款', '類別', '備註']
    : ['日期', '星期', '上班', '下班', '時數', '倍率', '金額', '備註'];
  const widths = isMonthly ? [12, 6, 10, 14, 10, 8, 10, 14, 10, 10, 10, 34] : [12, 6, 10, 14, 9, 7, 11, 34];
  ws.columns = widths.map((width) => ({ width }));
  ws.views = [{ state: 'frozen', ySplit: 3, showGridLines: false }];

  const lastCol = String.fromCharCode(64 + headers.length);
  ws.mergeCells(`A1:${lastCol}1`);
  put(ws, 'A1', `出勤明細　${y} 年 ${m} 月　${name}`, { size: 14, bold: true });
  ws.getRow(1).height = 28;
  headers.forEach((h, i) => {
    put(ws, `${String.fromCharCode(65 + i)}3`, h, { bold: true, color: WHITE, fill: INK, align: 'center', border: true });
  });
  ws.getRow(3).height = 22;

  const seen = new Set<string>();
  let r = 4;
  user.rows.forEach((row) => {
    const inT = row.in_time || (row.anomaly ? '未打上班卡' : '');
    const outT = row.out_time ? `${row.out_time}${row.out_date ? '（隔日）' : ''}` : (row.open ? '未打下班卡' : '');
    const hours = row.open || row.anomaly ? 0 : row.hours;
    let values: (string | number | null)[];
    let fill: string | undefined;
    if (isMonthly) {
      const d = daysByDate.get(row.date);
      const first = !!d && !seen.has(row.date);
      if (d) seen.add(row.date);
      const kind = d ? { weekday: '平日', rest_day: '休息日', holiday: '國定假日' }[d.kind] : '';
      values = [row.date, weekdayOf(row.date), inT, outT, hours,
        first ? d!.break : null, first ? d!.work : null, first ? d!.overtime : null,
        first && d!.short ? d!.short : null, first && d!.short_amount ? d!.short_amount : null,
        first ? kind : null, staffNote(row.note)];
      if (d?.kind === 'holiday' || d?.kind === 'rest_day') fill = HOLIDAY_FILL;
      if (first && d!.short > 0) fill = SHORT_FILL;
    } else {
      const mult = holidayDates.has(row.date) ? 2 : 1;
      values = [row.date, weekdayOf(row.date), inT, outT, hours, mult, Math.round(hours * rate * mult), staffNote(row.note)];
      if (mult === 2) fill = HOLIDAY_FILL;
    }
    if (row.open || row.anomaly) fill = SHORT_FILL;
    values.forEach((v, i) => {
      const col = String.fromCharCode(65 + i);
      const isNum = typeof v === 'number';
      const fmt = !isNum ? undefined
        : (isMonthly ? (i === 9 ? MONEY : HRS) : (i === 6 ? MONEY : i === 5 ? '0"×"' : HRS));
      put(ws, `${col}${r}`, v, {
        border: true, fill: fill ?? (r % 2 === 0 ? BAND : undefined), fmt,
        align: i <= 3 || (isMonthly && i === 10) ? 'center' : isNum ? 'right' : 'left',
        color: (row.open || row.anomaly) && (i === 2 || i === 3) ? RED : INK,
      });
    });
    r += 1;
  });

  const totals: (string | number | null)[] = isMonthly
    ? ['合計', null, null, null, user.total_hours, null, p.hours.worked ?? 0, null, p.hours.short ?? 0,
      (p.days || []).reduce((s, d) => s + d.short_amount, 0), null, null]
    : ['合計', null, null, null, user.total_hours, null, p.gross, null];
  totals.forEach((v, i) => {
    const isNum = typeof v === 'number';
    put(ws, `${String.fromCharCode(65 + i)}${r}`, v, {
      bold: true, fill: BAND, border: true, align: i === 0 ? 'center' : 'right',
      fmt: isNum ? ((isMonthly ? i === 9 : i === 6) ? MONEY : HRS) : undefined,
    });
  });
  ws.getRow(r).height = 22;
  ws.pageSetup = { paperSize: 9, orientation: 'landscape', fitToPage: true, fitToWidth: 1, fitToHeight: 0 };
}

function buildAlerts(ws: Worksheet, user: MonthlyUser) {
  const p = user.payroll!;
  ws.columns = [{ width: 12 }, { width: 9 }, { width: 76 }];
  ws.views = [{ showGridLines: false }];
  ws.mergeCells('A1:C1');
  put(ws, 'A1', '異常提醒（公司內部，發放薪資單前請刪除此分頁）', { size: 12, bold: true, color: RED });
  ['日期', '等級', '說明'].forEach((h, i) => {
    put(ws, `${'ABC'[i]}3`, h, { bold: true, color: WHITE, fill: INK, align: 'center', border: true });
  });
  const LEVEL = { error: ['錯誤', SHORT_FILL], warning: ['待確認', HOLIDAY_FILL], info: ['提醒', undefined] } as const;
  p.alerts.forEach((a, i) => {
    const r = 4 + i;
    const [label, fill] = LEVEL[a.level];
    put(ws, `A${r}`, a.date, { border: true, align: 'center', fill });
    put(ws, `B${r}`, label, { border: true, align: 'center', bold: a.level === 'error', fill });
    put(ws, `C${r}`, a.message, { border: true, wrap: true, fill });
  });
  if (p.alerts.length === 0) put(ws, 'A4', '本月無異常', { color: INK_LIGHT });
}

export function buildPayslipWorkbook(ExcelJS: ExcelJSModule, user: MonthlyUser, month: string): Workbook {
  const wb = new ExcelJS.Workbook();
  wb.creator = 'Metanoia OMS';
  buildSlip(wb.addWorksheet('薪資單'), user, month);
  buildDetail(wb.addWorksheet('出勤明細'), user, month);
  buildAlerts(wb.addWorksheet('異常提醒（內部）'), user);
  return wb;
}

export async function downloadPayslip(user: MonthlyUser, month: string): Promise<void> {
  if (!user.payroll) return;
  const ExcelJS = await import('exceljs');
  const wb = buildPayslipWorkbook(ExcelJS, user, month);
  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `薪資單_${user.employee_name || user.user_id}_${month}.xlsx`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
